/**
 * Channel Settings tab registry — single source for nav labels, routes, and admin gating.
 * Ref: UI-01_tab-registration.md | AUTOMATION_TAB_UI_SPEC.md v3.0 §1
 */

import { SHOW_WORKFLOW_TAB } from '../../helpers/constants';

export type ChannelSettingsTabId =
  | 'config'
  | 'members'
  | 'apps'
  | 'skills'
  | 'notifications'
  | 'automation'
  | 'workflow'
  | 'channelWorkflow';

export interface ChannelSettingsTabDef {
  id: ChannelSettingsTabId;
  label: string;
  /** When true, tab is only shown to channel admins */
  adminOnly: boolean;
  /** URL segment under `/channels/:channelId/` */
  path: string;
}

/** Feature flag: set to false to re-enable the Workflow tab in Channel Settings. */
const WORKFLOW_TAB_HIDDEN = false;

export const CHANNEL_SETTINGS_TAB_DEFS: readonly ChannelSettingsTabDef[] = [
  { id: 'config', label: 'Channel Config', adminOnly: false, path: 'config' },
  { id: 'members', label: 'Members', adminOnly: false, path: 'members' },
  { id: 'apps', label: 'Apps', adminOnly: false, path: 'apps' },
  { id: 'skills', label: 'Skills', adminOnly: false, path: 'skills' },
  { id: 'notifications', label: 'Notifications', adminOnly: false, path: 'notifications' },
  { id: 'automation', label: 'Automation', adminOnly: true, path: 'automation' },
  { id: 'workflow', label: 'Business Rules Workflow', adminOnly: true, path: 'workflow' },
  { id: 'channelWorkflow', label: 'Channel Workflow', adminOnly: true, path: 'channel-workflow' },
] as const;

/** Legacy numeric tab index used before tab-id registry (ThreadDetailPage deep links, etc.) */
export const LEGACY_TAB_INDEX_TO_ID: Record<number, ChannelSettingsTabId> = {
  0: 'config',
  1: 'members',
  2: 'apps',
  3: 'skills',
  4: 'notifications',
  5: 'automation',
  6: 'workflow',
  7: 'channelWorkflow',
};

const TAB_PARAM_ALIASES: Record<string, ChannelSettingsTabId> = {
  config: 'config',
  members: 'members',
  apps: 'apps',
  skills: 'skills',
  notification: 'notifications',
  notifications: 'notifications',
  automation: 'automation',
  workflow: 'workflow',
  'channel-workflow': 'channelWorkflow',
  channelworkflow: 'channelWorkflow',
};

export function getVisibleChannelSettingsTabs(
  isChannelAdmin: boolean,
  channelType?: 'email' | 'message' | 'voice',
): ChannelSettingsTabDef[] {
  return CHANNEL_SETTINGS_TAB_DEFS.filter((tab) => {
    // Business Rules Workflow is always available to channel admins. VITE_SHOW_WORKFLOW_TAB
    // controls only the legacy Channel Workflow tab.
    if (tab.id === 'channelWorkflow' && !SHOW_WORKFLOW_TAB) return false;
    // Automation requires label classification — not available for message channels.
    if (tab.id === 'automation' && channelType === 'message') return false;
    return !tab.adminOnly || isChannelAdmin;
  });
}

export function parseChannelSettingsTabParam(param: string | null | undefined): ChannelSettingsTabId | undefined {
  if (!param) return undefined;
  return TAB_PARAM_ALIASES[param.toLowerCase().trim()];
}

export function resolveInitialTabId(
  options: {
    initialTabId?: ChannelSettingsTabId;
    initialTab?: number;
    isChannelAdmin: boolean;
  }
): ChannelSettingsTabId {
  const visible = getVisibleChannelSettingsTabs(options.isChannelAdmin);
  const visibleIds = new Set(visible.map((t) => t.id));

  if (options.initialTabId && visibleIds.has(options.initialTabId)) {
    return options.initialTabId;
  }

  if (typeof options.initialTab === 'number') {
    const fromLegacy = LEGACY_TAB_INDEX_TO_ID[options.initialTab];
    if (fromLegacy && visibleIds.has(fromLegacy)) {
      return fromLegacy;
    }
  }

  return 'config';
}

export function getTabIndexById(tabs: ChannelSettingsTabDef[], id: ChannelSettingsTabId): number {
  const index = tabs.findIndex((t) => t.id === id);
  return index >= 0 ? index : 0;
}

/** Deep-link path, e.g. `/channels/abc-123/automation` */
export function getChannelSettingsTabPath(channelId: string, tabId: ChannelSettingsTabId): string {
  const def = CHANNEL_SETTINGS_TAB_DEFS.find((t) => t.id === tabId);
  return `/channels/${channelId}/${def?.path ?? 'config'}`;
}
